import React, { ReactElement } from 'react'

import { FormControlLabel, Switch, SwitchProps, SxProps } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'

export interface ToggleProps extends SwitchProps {
  highlighted?: boolean
  label?: string | ReactElement
  labelPlacement?: 'bottom' | 'end' | 'start' | 'top'
  switchClass?: string
  labelClass?: string
  className?: string
  sx?: SxProps
  inputSx?: SxProps
  tooltipText?: string
}

interface StyleProps {
  disabled: boolean
}

export const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  switchStyle: {
    padding: 8,
    '& .MuiSwitch-track': {
      borderRadius: 22 / 2,
      opacity: 1,
      backgroundColor: theme.palette.shades.shade03,
    },
    '& .Mui-disabled + .MuiSwitch-track': {
      opacity: 1,
    },
    '& .MuiSwitch-thumb': {
      backgroundColor: props.disabled ? theme.palette.grey.grey03 : theme.palette.common.white,
      filter: `drop-shadow(${theme.effectStyles.shadowStrong})`,
      width: 16,
      height: 16,
      margin: 2,
    },
    '& .Mui-checked+.MuiSwitch-track': {
      opacity: 1,
      background: props.disabled ? theme.palette.primary.light : theme.palette.primary.main,
    },
  },
  labelStyle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',

    [theme.breakpoints.between('md', 1200)]: {
      maxWidth: 80,
    },
  },
}))

/**
 * Toogle Component is used simple on/off toggle functionality
 * @beta this component is not yet finalized and might be subject to change.
 * @see {@link https://www.figma.com/design/2XkUgderL8Y8WDXHPhUXNJ/E3DC-Component-Catalog?node-id=12-5341&node-type=canvas&t=Oe03Eb0NL6CxgPx2-0 Figma Toggle}
 * @param label the label for the toggle
 * @param highlighted adds the primary color to the label
 * @param labelPlacement 'start' or 'end' places the label before or after the toggle
 * @param className to add additional styling to the toggle
 * @param formControllClass add additional styling to the label
 * @param switchProps see the @see {@link SwitchProps}
 */
const Toggle: React.FC<ToggleProps> = ({
  label,
  tooltipText,
  highlighted,
  labelPlacement = 'end',
  className,
  switchClass,
  labelClass,
  disabled = false,
  sx,
  inputSx,
  ...props
}) => {
  const { classes, cx } = useStyles({ disabled })

  return (
    <OptionalTooltipWrapper hasTooltip={disabled} placement={'top'} titleKey={tooltipText}>
      <FormControlLabel
        className={className}
        sx={sx}
        control={<Switch className={cx(classes.switchStyle, switchClass)} disabled={disabled} sx={inputSx} {...props} />}
        labelPlacement={labelPlacement}
        label={label ? <span className={cx(classes.labelStyle, labelClass)}>{label}</span> : undefined}
      />
    </OptionalTooltipWrapper>
  )
}

export default Toggle
